/**
 * Per-viewer composer settings. The console and the CRM it embeds share one
 * origin, so the React chat reads and writes the CRM chat window's own keys:
 * emoji shortcuts, recent emoji and the draft translation target stay the
 * same in both chats.
 */

const EMOJI_SHORTCUTS_KEY = 'crm_chat_emoji_shortcuts_v1';
const EMOJI_COLLAPSED_KEY = 'crm_chat_emoji_shortcuts_collapsed';
const EMOJI_RECENT_KEY = 'crm_chat_emoji_recent_v1';
const COMPOSER_TRANSLATE_KEY = 'crm_composer_translate_target';
const TRANSLATE_KEY = 'crm_translate_target';
const MESSAGE_TRANSLATE_KEY = 'crm_message_translate_target';
const LOCALE_KEY = 'crm_locale';
const AUTO_TRANSLATE_KEY = 'crm_chat_auto_translate';
const AUTO_TRANSLATE_PAUSE_KEY = 'crm_chat_auto_translate_paused_until';

export const DEFAULT_EMOJIS = ['😍', '😈', '🍆', '🍑', '🥵', '💋', '🥰', '🤤', '💕', '👅', '😘', '💦', '😇'];
const TRANSLATE_TARGETS = new Set(['en', 'ru', 'uk', 'fr', 'de', 'es', 'it', 'pt', 'pl', 'tr']);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

function readList(key: string, max: number): string[] | null {
  try {
    const parsed = JSON.parse(read(key) || 'null') as unknown;
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean).slice(0, max) : null;
  } catch {
    return null;
  }
}

export function emojiShortcuts(): string[] {
  const list = readList(EMOJI_SHORTCUTS_KEY, 24);
  return list && list.length ? list : DEFAULT_EMOJIS;
}

export function saveEmojiShortcuts(list: string[]) {
  write(EMOJI_SHORTCUTS_KEY, JSON.stringify(list.filter(Boolean).slice(0, 24)));
}

export function emojiBarCollapsed(): boolean {
  return read(EMOJI_COLLAPSED_KEY) === '1';
}

export function setEmojiBarCollapsed(collapsed: boolean) {
  write(EMOJI_COLLAPSED_KEY, collapsed ? '1' : '0');
}

export function recentEmoji(): string[] {
  return readList(EMOJI_RECENT_KEY, 24) || [];
}

export function rememberEmoji(emoji: string) {
  write(EMOJI_RECENT_KEY, JSON.stringify([emoji, ...recentEmoji().filter((e) => e !== emoji)].slice(0, 24)));
}

/** Language a draft is translated into (the CRM settings' "composer" target). */
export function composerTranslateTarget(): string {
  const code = String(read(COMPOSER_TRANSLATE_KEY) || read(TRANSLATE_KEY) || 'en').trim().toLowerCase();
  if (TRANSLATE_TARGETS.has(code)) return code;
  return code === 'ua' ? 'uk' : 'en';
}

/** Language messages are translated into (crm-settings.js getCrmMessageTranslateTarget): the saved one, else the CRM's language. */
export function messageTranslateTarget(): string {
  const saved = String(read(MESSAGE_TRANSLATE_KEY) || '').trim().toLowerCase();
  if (saved) return TRANSLATE_TARGETS.has(saved) ? saved : saved === 'ua' ? 'uk' : 'en';
  const locale = String(read(LOCALE_KEY) || 'ru').trim().toLowerCase();
  return ['ru', 'uk', 'en', 'es'].includes(locale) ? locale : locale === 'ua' ? 'uk' : 'ru';
}

/** Fan messages are translated as they come into view: on unless turned off (chat-composer-tools.js). */
export function autoTranslateOn(): boolean {
  return read(AUTO_TRANSLATE_KEY) !== '0';
}

export function setAutoTranslate(on: boolean) {
  write(AUTO_TRANSLATE_KEY, on ? '1' : '0');
}

/** MyMemory's daily quota ran out: auto-translation rests until tomorrow (chat-transcript.js). */
export function autoTranslatePaused(now = Date.now()): boolean {
  return now < Number(read(AUTO_TRANSLATE_PAUSE_KEY) || 0);
}

export function pauseAutoTranslateForToday() {
  const tomorrow = new Date();
  tomorrow.setHours(24, 0, 0, 0);
  write(AUTO_TRANSLATE_PAUSE_KEY, String(tomorrow.getTime()));
}

